import { useEffect } from "react";
import { Avatar } from "../../components/Avatar";
import { Btn } from "../../components/Button";
import { Pause, WifiOff } from "../../components/Icon";
import { useI18n } from "../../i18n";
import { asset } from "../../lib/asset";
import { narrator, preloadNarration } from "../../lib/narrator";
import type { RoomClient } from "../../lib/room-client";
import { BluffView } from "../bluff/BluffView";
import { RoomGuess } from "../guess/RoomGuess";
import { MatchView } from "../match/MatchView";
import { SpyView } from "../spy/SpyView";
import { TrufView } from "../truf/TrufView";
import { Lobby } from "./Lobby";
import { useAct, useRoomStatus, useRoomView } from "./useRoom";

export function RoomScreen({ client, tv }: { client: RoomClient; tv?: boolean }) {
  const { d } = useI18n();
  const view = useRoomView(client);
  const status = useRoomStatus(client);
  const act = useAct(client);

  // narrator speaks on the host's device, a TV screen, or a one-device game (unless the user chose otherwise)
  useEffect(() => {
    narrator.autoOn = !!(tv || client.local || view?.me?.isHost);
  }, [tv, client.local, view?.me?.isHost]);
  useEffect(() => {
    preloadNarration();
    return () => narrator.cancel();
  }, []);

  if (status === "gone") {
    const reason = client.goneReason();
    return (
      <div className="shell grid min-h-[70dvh] place-items-center text-center">
        <div>
          <Avatar index={15} size={130} mood="sad" disc={false} />
          <h1 className="display mt-6 text-4xl">{reason === "kicked" ? d.room.kicked : d.room.gone}</h1>
          <Btn className="mt-8" onClick={() => location.assign(asset(""))}>{d.room.toHome}</Btn>
        </div>
      </div>
    );
  }
  if (!view) {
    return <div className="grid min-h-[70dvh] place-items-center text-xl font-bold text-ink-3">{d.room.connecting}</div>;
  }

  return (
    <div className="relative min-h-dvh pb-10" style={{ paddingTop: "env(safe-area-inset-top)" }}>
      {status === "reconnecting" && (
        <div className="sticky top-0 z-30 flex items-center justify-center gap-2 bg-flame px-4 py-2 font-bold text-[#2a1200]">
          <WifiOff size={18} /> {d.room.reconnecting}
        </div>
      )}
      {view.phase === "lobby" ? (
        <Lobby client={client} view={view} tv={tv} />
      ) : view.game === "bluff" ? (
        <BluffView client={client} view={view} tv={tv} />
      ) : view.game === "spy" ? (
        <SpyView client={client} view={view} tv={tv} />
      ) : view.game === "match" ? (
        <MatchView client={client} view={view} tv={tv} />
      ) : view.game === "guess" ? (
        <RoomGuess client={client} view={view} tv={tv} />
      ) : (
        <TrufView client={client} view={view} tv={tv} />
      )}
      {view.paused && view.phase === "game" && (
        <div className="fixed inset-0 z-40 grid place-items-center bg-[#0c0418]/85 p-6 text-center">
          <div>
            <Pause size={56} className="mx-auto text-flame" />
            <p className="display mt-4 text-5xl">{d.room.paused}</p>
            <p className="mt-2 text-xl text-ink-2">{d.room.pausedSub}</p>
            {view.me?.isHost && (
              <Btn size="lg" className="mt-8" onClick={() => act({ t: "resume" })}>{d.room.resume}</Btn>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
